import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AccessGroup } from '@/api/auth';
import type { ConsiderationOnCard } from '@/api/people';
import { Button } from '@/components/ui/Button';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { StatusDot } from '@/components/ui/StatusDot';
import { cn } from '@/lib/cn';
import { mayWriteAboutPerson } from '@/pages/Candidates/candidateAccess';
import { vacancyIdentityLine } from '@/pages/Candidates/vacancyIdentity';
import { type } from '@/theme/typography';

import { onDate } from './cardDates';
import { ImportedContext } from './ImportedContext';
import { InterviewRow } from './InterviewRow';
import { ScheduleInterviewDialog } from './ScheduleInterviewDialog';
import { scheduleEmphasisFor } from './scheduleEmphasis';
import { StatusActionAnswer } from './StatusActionAnswer';
import { StatusActions } from './StatusActions';
import { useStatusAction } from './useStatusAction';

interface Props {
  consideration: ConsiderationOnCard;
  /** The card's person — an imported attachment is addressed by the person and the attachment. */
  personId: number;
  viewerId: number;
  /** Whether the status actions are rendered at all — §5's rule, not a permission check. */
  groups: AccessGroup[];
  /**
   * Whether this candidate's personal data has been erased (ADR 0009) — the card knows, and it is a
   * gate on **scheduling** here for the reason it is a gate on *Edit* in `CardMenu`: the erasure
   * closes that write path with a 409, so the control is not rendered rather than offered and refused.
   */
  anonymized: boolean;
}

/**
 * §6.5's *"Considerations. One block per consideration — vacancy, status, date opened"*, with the
 * interviews block inside it.
 *
 * **Two layers, divided by one hairline** (§6.5, *The block's composition*, TASK-123): an upper strip
 * carrying the run's identity and its next step — vacancy, status **beside** it, date opened, the
 * action row — and beneath it the record, which is the interviews block. Until 2026-08-11 this was one
 * `p-4` surface with three controls stacked as three consecutive blocks at one weight, and the status
 * pushed to the far edge of a `max-w-3xl` row from the vacancy it qualifies; read on the deployed dev
 * environment that is a white plane whose composition says nothing about which act is the ordinary
 * next step. The divider is `border-rule` and nothing else — §3 has no shadows and one radius.
 *
 * **One block per run, never one per person** — that is ADR 0002 made visible: a candidate returning
 * for a second vacancy is a second consideration on the same card, and their earlier round's
 * interviews are right here rather than on a card nobody can find. It is also why the summary row
 * above spans the whole person while each block below spans one run.
 *
 * ⚠️ **An Interviewer's card carries only the considerations they were assigned to** (backend
 * decision 7, `services.visible_considerations`). This component does not know that and must not add
 * a filter of its own on top of it: the reach is the server's, and a client-side second guess would
 * be a rule with no test behind it that silently hides a run the API deliberately sent.
 *
 * ⚠️ **The status is rendered, never chosen.** ADR 0010: status moves through the three actions —
 * `make-offer`, `hire`, `reject` — and there is no `status` field in any request body anywhere. They
 * are `StatusActions` in the strip, whose answer is `StatusActionAnswer` beneath it, and both read one
 * mutation from `useStatusAction`. The reason they are acts rather than a five-value `Select` is that a
 * dropdown makes `NEW → HIRED` a slip of the finger and makes every transition equally meaningless.
 * Which of the three a status offers is an **affordance** and not a rule: ADR 0010's table is total at
 * fifteen cells and the server refuses nothing on funnel grounds (`statusActions.ts`).
 *
 * ⚠️ **Scheduling is the run's fourth act and it is deliberately not one of the three** (TASK-081).
 * §6.5 makes it the **one action on view** for three reasons: it is the only act offered at all five
 * statuses, it is not a status action at all — `INTERVIEWING` is a consequence of a conversation
 * existing (ADR 0010), `statusActions.ts` knows nothing about this button, and it posts `InterviewIn`
 * to a different route entirely — and it is the act that produces feedback, which is what this module
 * exists for. **No wording here may ever say *"start interviewing"***.
 */
export function ConsiderationBlock({
  consideration,
  personId,
  viewerId,
  groups,
  anonymized,
}: Props) {
  const { t, i18n } = useTranslation('candidates');
  const [scheduling, setScheduling] = useState(false);

  /**
   * The three status actions as state, because §6.5 puts the control and the server's answer in two
   * different places in this block and they must remain one mutation (`useStatusAction`).
   */
  const statusAction = useStatusAction(consideration, groups);

  /**
   * §5, twice over. **Admin or Recruiter** — `Route("post", f"{CONSIDERATION}interviews/", …, (401,
   * 403, 201, 201, 403, 403))` in `backend/tests/test_permission_matrix.py`, its own dependency
   * (`SCHEDULE_INTERVIEW_ACCESS`) because scheduling is a recruiting act and an `Interviewer` keeps the
   * 403 even after TASK-029 widened their *read* access — so an Interviewer is shown nothing, hidden
   * and never disabled. And **not on an anonymised candidate**: the erasure closes this write path with
   * a 409 (ADR 0009), so it would be a control that cannot work.
   *
   * ⚠️ **The two gates are deliberately different and neither is widened here.** The erasure closes
   * scheduling and leaves the three status routes open — a run still has to be closable after one —
   * so an anonymised candidate's block keeps its status actions and loses this button alone.
   */
  const maySchedule = mayWriteAboutPerson(groups) && !anonymized;

  /** Whether the strip has a right-hand side at all — for an `Interviewer` it has none (§5). */
  const hasControls = statusAction.mayAct || maySchedule;

  /** The date itself, which the strip's sentence and the menu's accessible name both need. */
  const openedDate = onDate(consideration.opened_at, i18n.language);
  const openedOn = t('card.considerations.openedOn', { date: openedDate });

  /**
   * ⚠️ **Which vacancy this run is against** — the user's *"it is not clear whose this is"*,
   * answered (2026-11-03, TASK-188, ADR 0020 decision 9; the Russian they said it in is quoted in
   * `TASKS.md`, because everything under `src/` is written in English).
   * On the card the screenshot was taken from, two blocks rendered
   * the identical line *"Embedded Engineer · New · Opened Nov 3, 2026"* for two genuinely different
   * requisitions (1061 and 1055), and nothing on the card named or reached either.
   *
   * ⚠️ **The wording is `vacancyIdentity.ts`'s and not this file's**, shared with §6.3's filter —
   * one order of facts, one separator, one set of words (BUG-088). What belongs to this screen is
   * *where* the line sits, which is inside the block that carries `[overflow-wrap:anywhere]`.
   *
   * ⚠️ **A blinded viewer sees it, and that is a decision rather than an oversight** (feedback
   * protection #3, ADR 0006). A requisition's number and its manager are **metadata about the run,
   * not an assessment** — the gate hides assessments, not facts about the vacancy — and the payload
   * is where that is decided: `ConsiderationOnCardOut` carries both keys on the blinded shape too
   * (backend TASK-185). This component adds **no** discriminator of its own and must never grow
   * one; a client-side second guess is a rule with no test behind it.
   *
   * ⚠️ **It survives an erasure**, because it is the **vacancy's** data and not the candidate's:
   * ADR 0009 blanks the person's name, contacts and comment text and touches nothing on the
   * requisition. An anonymised card still says which run was which, which is the point of ADR 0009
   * keeping the record.
   */
  const vacancyIdentity = vacancyIdentityLine(
    {
      requisitionNumber: consideration.role_requisition_number,
      hiringManager: consideration.role_hiring_manager,
    },
    t,
  );

  return (
    <li className="rounded border border-rule bg-paper">
      {/*
       * The upper strip. `flex-wrap` with a full-width action row below `lg` is §10's rule as §6.5
       * writes it: the row drops beneath the identity block and takes the width rather than squeezing
       * three controls into a phone's worth of gutter.
       */}
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3 border-b border-rule px-4 py-3.5">
        {/*
         * ⚠️ **`[overflow-wrap:anywhere]` is what keeps one long vacancy title from moving the page**,
         * and it is the same fix `index.tsx`'s header block and `Table` carry for the same reason
         * (TASK-069). Measured in Chromium on the deployed bundle at 374 px: a **38-character
         * upper-case** title took `documentElement.scrollWidth` to **453** against a `clientWidth` of
         * **374** — the whole document scrolled sideways. The German 38-character title this was first
         * measured with **fitted** at 289 px, which is the trap: the measure is the rendered **width**,
         * not the character count, so a criterion phrased in characters can pass on a narrow word and
         * fail on a wide one. `min-w-0` alone is not enough — it lets the flex item shrink, but a value
         * with no break opportunity still sets the min-content width and paints past the box. `anywhere`
         * rather than `break-words` is the point: only `anywhere` counts break opportunities toward the
         * intrinsic minimum, which is the number the layout is sized from. It sits on the block that
         * holds the title, the status and the date, because `overflow-wrap` inherits.
         */}
        <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
          {/* The status stands **beside** the run it qualifies, not at the opposite edge of the card.
              `StatusDot` itself is untouched — §3's inline dot and pill, the same rendering §6.2,
              §6.3 and §6.7's other-interviews rows use. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h2 className={type.sectionHeading}>{consideration.role_title}</h2>
            <StatusDot value={consideration.status} />
          </div>

          {/*
           * **An absence is an absence** — no label, no dash, no empty scaffolding. A consideration
           * against a vacancy Panel's own screens created carries `""` in both keys, so the line is
           * empty and nothing at all is rendered in its place: the block reads exactly as it did
           * before this sprint, which is title, status and date opened.
           */}
          {vacancyIdentity !== '' && <p className={cn(type.metadata, 'mt-1')}>{vacancyIdentity}</p>}

          {hasControls && <p className={cn(type.metadata, 'mt-1')}>{openedOn}</p>}
        </div>

        {hasControls ? (
          <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto">
            {maySchedule && (
              /*
               * §6.5: the one action on view, and its **emphasis** follows the status while its
               * availability never does (`scheduleEmphasis.ts`) — it is offered at all five.
               */
              <Button
                size="sm"
                variant={scheduleEmphasisFor(consideration.status)}
                className="flex-1 lg:flex-none"
                onClick={() => setScheduling(true)}
              >
                {t('card.schedule.open')}
              </Button>
            )}

            {statusAction.mayAct && (
              <StatusActions
                state={statusAction}
                vacancy={consideration.role_title}
                openedDate={openedDate}
              />
            )}
          </div>
        ) : (
          /* No right-hand side to fill, so the date takes the right edge and the strip does not read
             as truncated (§6.5's `Interviewer` case). */
          <p className={type.metadata}>{openedOn}</p>
        )}
      </div>

      {/* §6.5: the server's answer stays on the card, beneath the strip and above the interviews —
          never in the control that caused it, and never a `Toast`. */}
      {statusAction.mayAct && <StatusActionAnswer state={statusAction} />}

      {/* Mounted only while open, as every dialog on this card is: a cancelled form leaves nothing
          behind, and the answer of one attempt is not the starting state of the next. */}
      {scheduling && (
        <ScheduleInterviewDialog
          consideration={consideration}
          viewerId={viewerId}
          // The groups go with it because the dialog's staff read is gated on them and not only its
          // rendering (ADR 0016) — the demotion-without-a-re-login path `VacancyFilter` closes too.
          groups={groups}
          onClose={() => setScheduling(false)}
        />
      )}

      {/* The lower sheet: what ADP sent about this run, and then the record of it. */}
      <div className="p-4">
        {/*
         * ⚠️ **What came with the run, before the record of it** (Sprint 24, ADR 0020 decision 3).
         * An imported attachment is the document an interviewer reads to prepare and an imported
         * comment is the note the recruiter already acted on, so both sit above the conversations
         * rather than below them. Each block disappears with its last item, and the comments block
         * is **absent entirely** — no heading, no count — for a viewer ADR 0006's gate has not
         * opened (`ImportedContext`).
         */}
        <ImportedContext consideration={consideration} personId={personId} />

        {/* §3's eyebrow carries a count, and this one did not until TASK-124. The run's interview
            total is here and **only** here — the strip's metadata line does not repeat it. */}
        <Eyebrow count={consideration.interviews.length}>{t('card.interviews.label')}</Eyebrow>

        {consideration.interviews.length === 0 ? (
          /* One sentence, not an `EmptyState` card: a block inside a block reads as a problem, and
             a run with no conversation yet is the ordinary state of a new candidate. */
          <p className="text-sm text-ink-500">{t('card.interviews.none')}</p>
        ) : (
          <ul className="divide-y divide-rule border-y border-rule">
            {/* In the order the API sends them. Nothing is sorted here: the ordering is the
                backend's (`Interview.Meta.ordering`), and re-sorting on the client is how two
                screens end up disagreeing about which conversation was the latest. */}
            {consideration.interviews.map((interview) => (
              <InterviewRow key={interview.id} interview={interview} viewerId={viewerId} />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}
